import type { AxiosInstance } from "axios";
import { PropertyStatus } from "../global/propertyStatus";

const DRAFT_STATUS_FILTER = "pre_registered";
const DRAFTS_PER_PAGE = 35;

export interface DraftSaveResult {
  id: number;
  status?: string;
  user_id?: number;
}

export interface PropertyDraftMeta {
  isDraft: boolean;
  canPublish: boolean;
  publishBlockers: string[];
}

export interface FetchPropertyDraftResult extends PropertyDraftMeta {
  property: Record<string, unknown>;
  rentReasons: unknown[];
}

export interface DraftListItem {
  id: number;
  title: string;
  is_rented: number;
  rent_value: string;
  address: string;
  neighborhood: string;
  number: string;
  status: PropertyStatus;
  updated_at?: string;
}

export interface FetchDraftsResult {
  drafts: DraftListItem[];
  lastPage: number;
}

interface RawDraftItem {
  id: number;
  title?: string | null;
  address?: string | null;
  neighborhood?: string | null;
  number?: string | null;
  rent_value?: number | string | null;
  last_draft_updated_at?: string | null;
  pre_registered_at?: string | null;
}

/**
 * Draft save responses arrive as either `{ data: { id, status } }` or the
 * nested shape `{ success, data: { data: { id, status } }, message }`.
 */
function extractUserId(record: Record<string, unknown>): number | undefined {
  const candidates: unknown[] = [
    record.user_id,
    (record.property as Record<string, unknown> | undefined)?.user_id,
  ];

  for (const candidate of candidates) {
    const parsed = Number(candidate);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
  }

  return undefined;
}

function extractSavedDraft(responseData: unknown): DraftSaveResult {
  const inner = (responseData as { data?: unknown })?.data;

  if (!inner || typeof inner !== "object") {
    return { id: NaN };
  }

  const record = inner as Record<string, unknown>;

  if (record.id != null) {
    return {
      id: Number(record.id),
      status: record.status as string | undefined,
      user_id: extractUserId(record),
    };
  }

  const nested = record.data as Record<string, unknown> | undefined;

  return {
    id: Number(nested?.id),
    status: nested?.status as string | undefined,
    user_id: nested ? extractUserId(nested) : undefined,
  };
}

function buildMinimalDraftPayload(
  payload: Record<string, unknown>
): Record<string, unknown> {
  return {
    property_type_id: payload.property_type_id,
    address_id: payload.address_id,
  };
}

function hasPayloadBeyondMinimum(payload: Record<string, unknown>): boolean {
  const keys = Object.keys(payload).filter(
    (key) => payload[key] !== undefined && payload[key] !== null && payload[key] !== ""
  );

  return keys.some((key) => key !== "property_type_id" && key !== "address_id");
}

async function createDraft(
  api: AxiosInstance,
  payload: Record<string, unknown>
): Promise<DraftSaveResult> {
  const response = await api.post(
    "/api/adm/properties/draft",
    buildMinimalDraftPayload(payload)
  );
  const created = extractSavedDraft(response.data);

  if (!Number.isFinite(created.id)) {
    throw new Error("Property ID missing from draft create response");
  }

  return created;
}

/**
 * Updates an existing pré-cadastro (draft). Always uses
 * `PUT /api/adm/properties/{property}/draft` — never the published-property PUT.
 * Extra payload fields are merged into the draft on the server.
 */
export async function updatePropertyDraft(
  api: AxiosInstance,
  propertyId: number,
  payload: Record<string, unknown>
): Promise<DraftSaveResult> {
  if (!Number.isFinite(propertyId) || propertyId <= 0) {
    throw new Error("A valid property id is required to update a draft");
  }

  const response = await api.put(
    `/api/adm/properties/${propertyId}/draft`,
    payload
  );
  const result = extractSavedDraft(response.data);

  return {
    id: Number.isFinite(result.id) ? result.id : propertyId,
    status: result.status,
    user_id: result.user_id,
  };
}

export async function saveDraft(
  api: AxiosInstance,
  payload: Record<string, unknown>,
  propertyId: number | null
): Promise<DraftSaveResult> {
  if (propertyId != null && Number.isFinite(propertyId) && propertyId > 0) {
    return updatePropertyDraft(api, propertyId, payload);
  }

  const created = await createDraft(api, payload);

  if (!hasPayloadBeyondMinimum(payload)) {
    return created;
  }

  const updated = await updatePropertyDraft(api, created.id, payload);

  return {
    id: updated.id || created.id,
    status: updated.status ?? created.status,
    user_id: updated.user_id,
  };
}

export async function publishProperty(
  api: AxiosInstance,
  propertyId: number,
  payload: Record<string, unknown> = {}
) {
  const response = await api.post(
    `/api/adm/properties/${propertyId}/publish`,
    payload
  );
  return response.data?.data;
}

function mapDraftItem(item: RawDraftItem): DraftListItem {
  return {
    id: item.id,
    title: item.title ?? "",
    is_rented: 0,
    rent_value: item.rent_value != null ? String(item.rent_value) : "0",
    address: item.address ?? "",
    neighborhood: item.neighborhood ?? "",
    number: item.number ?? "",
    status: PropertyStatus.PRE_REGISTERED,
    updated_at: item.last_draft_updated_at ?? item.pre_registered_at ?? undefined,
  };
}

function resolveLastPage(payload: {
  last_page?: number;
  total?: number;
  per_page?: number;
}): number {
  if (payload.last_page != null) return payload.last_page;
  if (payload.total != null && payload.per_page) {
    return Math.max(1, Math.ceil(payload.total / payload.per_page));
  }
  return 1;
}

export async function fetchPropertyDraft(
  api: AxiosInstance,
  propertyId: number
): Promise<FetchPropertyDraftResult> {
  const response = await api.get(`/api/adm/properties/${propertyId}`);
  const payload = response.data?.data ?? {};

  return {
    property: (payload.property as Record<string, unknown>) ?? {},
    rentReasons: Array.isArray(payload.rentReasons) ? payload.rentReasons : [],
    isDraft: Boolean(payload.is_draft),
    canPublish: Boolean(payload.can_publish),
    publishBlockers: Array.isArray(payload.publish_blockers)
      ? payload.publish_blockers
      : [],
  };
}

export async function fetchDrafts(
  api: AxiosInstance,
  page: number,
  search?: string
): Promise<FetchDraftsResult> {
  const response = await api.get("/api/adm/properties", {
    params: {
      status: DRAFT_STATUS_FILTER,
      page,
      per_page: DRAFTS_PER_PAGE,
      search: search || undefined,
    },
  });

  const payload = response.data?.data ?? {};
  const rawList: RawDraftItem[] = Array.isArray(payload.data) ? payload.data : [];

  return {
    drafts: rawList.map(mapDraftItem),
    lastPage: resolveLastPage(payload),
  };
}
